import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { quickAnswerService } from '../../../services/quickAnswerService';
import { useAuth } from '../../../contexts/AuthContext';

const QuickAnswersPanel = ({ fieldTarget = 'motivo_visita', onSelect, label = 'Respuestas Rápidas' }) => {
  const { userProfile } = useAuth();
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManage, setShowManage] = useState(false);
  const [newAnswer, setNewAnswer] = useState({ label: '', value: '' });
  const [saving, setSaving] = useState(false);
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ label: '', value: '' });
  const [editSaving, setEditSaving] = useState(false);

  const companyId = userProfile?.companyId;

  useEffect(() => {
    loadAnswers();
  }, [companyId, fieldTarget]);

  const loadAnswers = async () => {
    setLoading(true);
    try {
      const data = await quickAnswerService?.getAll(companyId, fieldTarget);
      setAnswers(data || []);
    } catch (err) {
      console.error('[QuickAnswersPanel] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newAnswer?.label?.trim() || !newAnswer?.value?.trim()) {
      alert('Etiqueta y valor son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await quickAnswerService?.create({
        companyId,
        label: newAnswer?.label,
        value: newAnswer?.value,
        fieldTarget
      });
      setNewAnswer({ label: '', value: '' });
      // Reload from DB immediately to reflect new answer
      await loadAnswers();
    } catch (err) {
      alert('Error al guardar: ' + (err?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await quickAnswerService?.delete(id);
      // Reload from DB immediately
      await loadAnswers();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const handleStartEdit = (ans) => {
    setEditingId(ans?.id);
    setEditData({ label: ans?.label || '', value: ans?.value || '' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({ label: '', value: '' });
  };

  const handleSaveEdit = async (id) => {
    if (!editData?.label?.trim() || !editData?.value?.trim()) {
      alert('Etiqueta y valor son obligatorios');
      return;
    }
    setEditSaving(true);
    try {
      await quickAnswerService?.update(id, {
        label: editData?.label,
        value: editData?.value
      });
      setEditingId(null);
      setEditData({ label: '', value: '' });
      // Reload from DB immediately so chips reflect updated text
      await loadAnswers();
    } catch (err) {
      alert('Error al actualizar: ' + (err?.message || ''));
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return null;
  if (answers?.length === 0 && !showManage) return null;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <Icon name="Zap" size={12} className="text-accent" />
          {label}
        </span>
        <button
          type="button"
          onClick={() => setShowManage(!showManage)}
          className="text-xs text-primary hover:underline flex items-center gap-1"
        >
          <Icon name={showManage ? 'ChevronUp' : 'Settings'} size={12} />
          {showManage ? 'Cerrar' : 'Editar'}
        </button>
      </div>

      {/* Quick answer chips — always read from state (which is always reloaded from DB after any change) */}
      {!showManage && (
        <div className="flex flex-wrap gap-2">
          {answers?.map(ans => (
            <button
              key={ans?.id}
              type="button"
              onClick={() => {
                onSelect?.(ans?.value);
              }}
              className="px-3 py-1 text-xs rounded-full border border-border bg-muted hover:bg-primary/10 hover:border-primary/50 hover:text-primary transition-smooth"
            >
              {ans?.label}
            </button>
          ))}
        </div>
      )}

      {/* Manage panel */}
      {showManage && (
        <div className="bg-muted/30 rounded-lg border border-border p-3 space-y-3">
          <div className="space-y-2">
            {answers?.map(ans => (
              <div key={ans?.id} className="bg-card rounded border border-border">
                {editingId === ans?.id ? (
                  // Edit mode for this answer
                  <div className="p-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Etiqueta"
                        value={editData?.label}
                        onChange={(e) => setEditData(p => ({ ...p, label: e?.target?.value }))}
                      />
                      <Input
                        placeholder="Texto completo"
                        value={editData?.value}
                        onChange={(e) => setEditData(p => ({ ...p, value: e?.target?.value }))}
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:bg-muted transition-smooth"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(ans?.id)}
                        disabled={editSaving}
                        className="px-2 py-1 text-xs rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-smooth flex items-center gap-1"
                      >
                        {editSaving ? <Icon name="Loader2" size={11} className="animate-spin" /> : <Icon name="Check" size={11} />}
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode for this answer
                  <div className="flex items-center justify-between p-2">
                    <div>
                      <span className="text-sm font-medium text-foreground">{ans?.label}</span>
                      <p className="text-xs text-muted-foreground">{ans?.value}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(ans)}
                        className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-smooth"
                        title="Editar"
                      >
                        <Icon name="Pencil" size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(ans?.id)}
                        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-smooth"
                        title="Eliminar"
                      >
                        <Icon name="Trash2" size={13} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">Agregar nueva respuesta rápida</p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Etiqueta (ej: Mantenimiento)"
                value={newAnswer?.label}
                onChange={(e) => setNewAnswer(p => ({ ...p, label: e?.target?.value }))}
              />
              <Input
                placeholder="Texto completo"
                value={newAnswer?.value}
                onChange={(e) => setNewAnswer(p => ({ ...p, value: e?.target?.value }))}
              />
            </div>
            <Button variant="default" size="sm" iconName="Plus" iconPosition="left" className="mt-2" onClick={handleAdd} disabled={saving} loading={saving}>
              Agregar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickAnswersPanel;
